import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { translate } from "shared/languages";
import { BookScheduleForm } from "shared/types";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import { windowConflictOf } from "shared/utils/windowConflict";
import { WindowConflict } from "shared/types";
import { tzDate } from "shared/utils/dayjs";
import { invalidateCustomerList } from "hooks/mutations/customerList";
import {
  bookOpenClassV2,
  undoBookingByIdV2,
  updateBookingByIdV2,
  updateBookingsByIdsV2,
} from "hooks/api/booking-v2";

// After a swap or an extend other classes' bookings, or the package's end
// date, changed too: refresh them all rather than one slot.
const invalidateAfterConflictChoice = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: ["booking-v2"] });
  queryClient.invalidateQueries({ queryKey: ["waitlist-v2"] });
  queryClient.invalidateQueries({ queryKey: ["schedule-v2"] });
  queryClient.invalidateQueries({ queryKey: ["customer_package"] });
};

// onWindowConflict: the booking would end a first-class package before a
// class already on it; the caller opens WindowConflictModal and retries with
// on_conflict instead of showing the refusal as a toast.
export const useBookOpenClassV2 = (
  onWindowConflict?: (conflict: WindowConflict, form: BookScheduleForm) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: BookScheduleForm) => bookOpenClassV2(form),
    onSuccess: (response, form) => {
      // A booking spends or returns a credit; the Klien list counts them.
      invalidateCustomerList(queryClient);
      const { schedule_id, occurrence_timestamp } = response.data;
      queryClient.invalidateQueries({
        queryKey: ["booking-v2", "schedule", schedule_id, occurrence_timestamp],
      });
      if (form.on_conflict) invalidateAfterConflictChoice(queryClient);
      getApiSuccessMessage(
        `Booking ${translate("messages.success.create")}`,
      );
    },
    onError: (error: AxiosError<ErrorResponse>, form) => {
      const conflict = windowConflictOf(error);
      if (conflict && onWindowConflict && !form.on_conflict) {
        onWindowConflict(conflict, form);
        return;
      }
      getApiErrorMessage(error);
    },
  });
};

export const useUpdateBookingByIdV2 = (
  onPromotion?: (promoted: {
    id: string;
    customer_name: string;
    schedule_id: string;
    occurrence_timestamp: string;
  }) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      newStatus,
    }: {
      bookingId: string;
      newStatus: string;
    }) => updateBookingByIdV2(bookingId, newStatus),
    onSuccess: (response) => {
      // A booking spends or returns a credit; the Klien list counts them.
      invalidateCustomerList(queryClient);
      const { schedule_id, schedule_date, promoted_booking } = response.data;
      const startWeek = tzDate(schedule_date)
        .startOf("week")
        .toDate()
        .toISOString();
      queryClient.invalidateQueries({
        queryKey: ["booking-v2", "schedule", schedule_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["waitlist-v2", "schedule", schedule_id],
      });
      queryClient.invalidateQueries({ queryKey: ["schedule-v2", startWeek] });
      getApiSuccessMessage(
        `Booking ${translate("messages.success.update")}`,
      );
      if (promoted_booking && onPromotion) {
        onPromotion(promoted_booking);
      }
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

type UndoForm = { bookingId: string; on_conflict?: "swap" | "extend" };

export const useUndoBookingByIdV2 = (
  onWindowConflict?: (conflict: WindowConflict, form: UndoForm) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, on_conflict }: UndoForm) =>
      undoBookingByIdV2(bookingId, on_conflict),
    onSuccess: (response, form) => {
      if (form.on_conflict) invalidateAfterConflictChoice(queryClient);
      // A booking spends or returns a credit; the Klien list counts them.
      invalidateCustomerList(queryClient);
      const { schedule_id, schedule_date } = response.data;
      const startWeek = tzDate(schedule_date)
        .startOf("week")
        .toDate()
        .toISOString();
      queryClient.invalidateQueries({
        queryKey: ["booking-v2", "schedule", schedule_id],
      });
      queryClient.invalidateQueries({ queryKey: ["schedule-v2", startWeek] });
      getApiSuccessMessage(`Booking ${translate("messages.success.update")}`);
    },
    onError: (error: AxiosError<ErrorResponse>, form) => {
      const conflict = windowConflictOf(error);
      if (conflict && onWindowConflict && !form.on_conflict) {
        onWindowConflict(conflict, form);
        return;
      }
      getApiErrorMessage(error);
    },
  });
};

export const useUpdateBookingsByIdsV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingIds,
      newStatus,
    }: {
      bookingIds: string[];
      newStatus: string;
    }) => updateBookingsByIdsV2(bookingIds, newStatus),
    onSuccess: (response) => {
      // A booking spends or returns a credit; the Klien list counts them.
      invalidateCustomerList(queryClient);
      const schedule_id = response.data.schedule_id;
      queryClient.invalidateQueries({
        queryKey: ["booking-v2", "schedule", schedule_id],
      });
      getApiSuccessMessage(
        `Booking ${translate("messages.success.update")}`,
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};
